import { ModalType } from '@/Enums/UserManagement/ModalType.enum';
import { Role } from '@/Enums/Users/Role.enum';
import {
  getClients,
  getScreensByRole,
  getSelectedUser,
  getUsers,
  getWhitelabels,
  resetSelectedUser,
  resetUserManagementPageState,
} from '@/store/slices/userManagementSlice';
import { Card } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import UserModal from './UserModals';
import RemoveUserConfirmation from './UserModals/RemoveUserConfirmation';
import UsersGrid from './UsersTable';

const UserManagement = () => {
  const dispatch = useDispatch();

  const { role: meRole, userProfile, whitelabel: meWhitelabel } = useSelector(state => state.me);
  const { usersTotal, filters, usersRequestId, clientsRequestId, selectedUserId, screensByRoleLoaded, users } =
    useSelector(state => state.userManagement);

  // #region NEW USER MODAL
  const [openNewUserModal, setOpenNewUserModal] = useState(false);
  const handleOpenNewUserModal = () => {
    if (!screensByRoleLoaded) {
      dispatch(getScreensByRole());
    }
    setOpenNewUserModal(true);
  };
  const handleCloseNewUserModal = userAdded => {
    setOpenNewUserModal(false);
    if (userAdded) {
      dispatch(getUsers());
    }
  };
  // #endregion

  // #region USERS
  useEffect(() => {
    dispatch(resetUserManagementPageState());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getUsers());
  }, [dispatch, filters]);

  const [removedUser, setRemovedUser] = useState(null);
  const onRemoveModalClose = useCallback((userId = null) => setRemovedUser(userId), []);
  const handleRemoveUserClose = () => onRemoveModalClose();
  const onRemoveConfirm = () => {
    onRemoveModalClose();
    dispatch(getUsers());
  };
  // #endregion

  // #region CLIENTS
  useEffect(() => {
    dispatch(getClients());
  }, [dispatch]);
  // #endregion

  // #region WHITELABELS
  useEffect(() => {
    if (meRole !== Role.WHITELABEL_ADMIN) {
      dispatch(getWhitelabels());
    }
  }, [dispatch, meRole]);
  // #endregion

  // #region VIEW USER
  const [openViewProfileModal, setOpenViewProfileModal] = useState(false);
  const handleOpenViewProfileModal = useCallback(() => {
    if (!screensByRoleLoaded) {
      dispatch(getScreensByRole());
    }
    setOpenViewProfileModal(true);
  }, [dispatch, screensByRoleLoaded]);
  const handleCloseViewProfileModal = userUpdated => {
    setOpenViewProfileModal(false);
    dispatch(resetSelectedUser());
    if (userUpdated) {
      dispatch(getUsers());
    }
  };

  useEffect(() => {
    if (selectedUserId) {
      dispatch(getSelectedUser());
      handleOpenViewProfileModal();
    }
  }, [selectedUserId, dispatch, handleOpenViewProfileModal]);
  // #endregion

  return (
    <div className='w-full p-[30px]'>
      <Card className='h-full'>
        <UsersGrid
          handleOpenNewUserModal={handleOpenNewUserModal}
          rowCount={usersTotal}
          data={users}
          removeUser={onRemoveModalClose}
          loading={!!(clientsRequestId || usersRequestId)}
          filters={filters}
          meId={userProfile.id}
          meRole={meRole}
        />
        <UserModal
          open={openNewUserModal}
          handleClose={handleCloseNewUserModal}
          meRole={meRole}
          meWhitelabel={meWhitelabel}
          modalType={ModalType.New}
        />
        <UserModal
          open={openViewProfileModal}
          handleClose={handleCloseViewProfileModal}
          meRole={meRole}
          meWhitelabel={meWhitelabel}
          modalType={ModalType.View}
        />
        <RemoveUserConfirmation
          userId={removedUser}
          open={!!removedUser}
          onConfirm={onRemoveConfirm}
          handleClose={handleRemoveUserClose}
        />
      </Card>
    </div>
  );
};

export default UserManagement;
